import { t } from 'state/intl/translations';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { ORDER_TTL } from './constants';
import { StoredOrderId } from './types';
export const getNativePaymentDisclaimer = (
  allowApplePay: boolean,
  allowGooglePay: boolean
): string | undefined => {
  let nativePaymentDisclaimerId;
  if (!allowApplePay && !allowGooglePay) {
    nativePaymentDisclaimerId = t('nativePaymentNotAvailable');
  } else if (allowApplePay && !allowGooglePay) {
    nativePaymentDisclaimerId = t('googlePayPaymentNotAvailable');
  } else if (!allowApplePay && allowGooglePay) {
    nativePaymentDisclaimerId = t('applePayPaymentNotAvailable');
  }
  return nativePaymentDisclaimerId;
};

export function getStoredOrderId() {
  const now = new Date().getTime();
  const stored = LocalStorage.getItem<StoredOrderId>(StorageKeys.ORDER_ID);

  // If no rbiOrderId and no stored value, return false
  if (!stored) {
    return;
  }

  if (stored.lastUpdate < now - ORDER_TTL) {
    // Stored order is expired so clear the local storage and return false
    LocalStorage.removeItem(StorageKeys.ORDER_ID);
    return;
  }

  return stored.id;
}

/**
 * This function manages order ID persistence in local storage with TTL (time-to-live) validation.
 * It handles multiple scenarios:
 * - If no order ID is provided and none is stored, returns false
 * - If no order ID is provided but one exists in storage, validates it against TTL
 * - If an order ID is provided but none is stored, stores it with current timestamp
 * - If both order ID and stored data exist, validates and updates as needed
 *
 * The function automatically cleans up expired orders from local storage.
 *
 * @param rbiOrderId - The RBI order ID to validate, or null if none provided
 * @returns true if the order ID is valid and not expired, false otherwise
 */
export function isRbiOrderValid(rbiOrderId: string | null): boolean {
  const now = new Date().getTime();
  const stored = LocalStorage.getItem<StoredOrderId>(StorageKeys.ORDER_ID);

  // If no rbiOrderId and no stored value, return false
  if (!rbiOrderId && !stored) {
    return false;
  }

  // If no rbiOrderId but stored value exists
  if (!rbiOrderId && stored) {
    if (stored.lastUpdate < now - ORDER_TTL) {
      // Stored order is expired so clear the local storage and return false
      LocalStorage.removeItem(StorageKeys.ORDER_ID);
      return false;
    }

    return true;
  }

  if (rbiOrderId && !stored) {
    // rbiOrderId exists but no stored value so store it with the current timestamp
    LocalStorage.setItem(StorageKeys.ORDER_ID, { id: rbiOrderId, lastUpdate: now });
    return true;
  }

  if (rbiOrderId && stored) {
    // If stored order ID doesn't match current rbiOrderId, update the stored value
    if (stored.id !== rbiOrderId) {
      LocalStorage.setItem(StorageKeys.ORDER_ID, { id: rbiOrderId, lastUpdate: now });
      return true;
    }

    // If the stored order is expired, clear the local storage and return false
    if (stored.lastUpdate < now - ORDER_TTL) {
      LocalStorage.removeItem(StorageKeys.ORDER_ID);
      return false;
    }

    // Otherwise, the stored data is valid
    return true;
  }

  return false;
}
